/**
 * @file coupon-status.ts
 *
 * Status vocabulary for the Coupons tab: the derived active/expired state and the
 * accessors that resolve it to a Gravity indicator colour (consumed by the shared
 * {@link StatusBadge}) and a localized label. The colour/label maps are
 * module-private; callers use {@link statusIndicator} / {@link statusLabel} so the
 * mapping lives in one place. Kept separate from the formatting helpers in
 * `coupon-list.ts`, mirroring `orders/order-status.ts` vs `orders/order-list.ts`.
 */

import type { MessageDescriptor } from 'react-intl';
import { adminMessages } from '../i18n/messages';
import type { ShopCoupon } from '../api/shop-types';

/** A coupon's effective state for the status pill. */
export type CouponStatus = 'active' | 'expired';

/**
 * Computes a coupon's effective status: `"expired"` when its expiry date has
 * passed or its usage limit has been reached, otherwise `"active"`.
 *
 * @param coupon - The coupon.
 * @param now - The reference time (defaults to the current time; injectable for tests).
 *
 * @returns The coupon's status.
 */
export function couponStatus(coupon: ShopCoupon, now: Date = new Date()): CouponStatus {
  if (coupon.date_expires) {
    const expires = new Date(coupon.date_expires);
    if (!Number.isNaN(expires.getTime()) && expires.getTime() < now.getTime()) {
      return 'expired';
    }
  }
  if (coupon.usage_limit != null && coupon.usage_count >= coupon.usage_limit) {
    return 'expired';
  }
  return 'active';
}

/** Gravity indicator class per status, consumed by the shared {@link StatusBadge}. */
const STATUS_INDICATOR: Record<CouponStatus, string> = {
  active: 'gv-state-positive',
  expired: 'gv-state-critical',
};

/** Localized label descriptor per status. */
const STATUS_LABELS: Record<CouponStatus, MessageDescriptor> = {
  active: adminMessages.couponsStatusActive,
  expired: adminMessages.couponsStatusExpired,
};

/**
 * Resolves the Gravity indicator class for a coupon status.
 *
 * @param status - The coupon status.
 *
 * @returns The `gv-state-*` class.
 */
export function statusIndicator(status: CouponStatus): string {
  return STATUS_INDICATOR[status];
}

/**
 * Resolves the localized label descriptor for a coupon status.
 *
 * @param status - The coupon status.
 *
 * @returns The label {@link MessageDescriptor}.
 */
export function statusLabel(status: CouponStatus): MessageDescriptor {
  return STATUS_LABELS[status];
}
